import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Params } from '@angular/router';
import { OrganizationList, ReportData, SeniorCenter, OrganizationAddressResponse, OrganizationFilters } from '@app/models/organization.model';
import { SaveActivationSetupRulePayload, OrgActivationSetupResponse, ActivatedUsersResponse, InactiveUsersCountResponse } from '@app/models/activate-users.model';
import {
    AccountFeatureLimitsData,
    AccountLimitsData,
    AppStatus,
    ConstData,
    CreateTagData,
    DialogData,
    GeneralListDataResponse,
    InterestAssociateWarnDialogModel,
    ServiceDownBannerData,
    TableFilterData,
    TagData,
    TimeData
} from '@app/models/shared.model';
import { DialogComponent } from '@app/shared/components/dialog/dialog.component';
import {
    SNACK_TIMEOUT,
    SNACKBAR_NOTIFICATION,
    DEFAULT_TIMEZONE,
    NoProgramFilterValue,
    appValues,
    GENERAL_MESSAGES,
    DATE_TIME_FORMATS,
    MANAGE_INTERESTS_DATA,
    AnalyticAPIReqLabels
} from '@app/shared';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, map, shareReplay, switchMap, catchError, filter, finalize } from 'rxjs/operators';
import { HttpService } from './http.service';
import { AppProfileData, AuthData, CurrentUser, PortalUser, PortalUserListItem } from '@app/models/profile.model';
import {
    GeneralDataResponse,
    UserInterest,
    UserInterestAssociatedWarn,
    UserInterestDetail,
    UserInterestPost,
    BulkInterestStatusPayload,
    BulkInterestStatusResponse,
    ScheduledCommsCheckResponse,
    InterestDuplicateCheckPayload,
    UpdateOrgInterestPayload,
    OrgInterestScheduledCommunications
} from '@app/models/user.model';
import { LocalStorageService } from './local-storage.service';
import { environment } from '@environments/environment';
import { AudienceDedupCheckRequest, AudienceDedupCheckResponse, GeneralResponse, PreviewLanguageTranslationData } from '@app/models';
import { MomentTimezonePipe } from '@app/shared/pipes/utcToTimezone.pipe';
import * as moment from 'moment-timezone';
import { UserLabel } from '@app/models/label.model';
import { CreateCustomField, CreateCustomFieldPayload, CustomField, CustomFieldStatus, CustomFieldsScheduledCommunicationResponse, UpdateCustomFieldStatus } from '@app/models/custom-fields.model';
import { SelfServiceProgram, TimezoneResponse, OrgServiceTypesResponse, CreateServiceData, CreateProgramData, ProgramUsersResponse, OrgUserForProgramResponse, UpdateProgramUsersPayload } from '@app/models/program-management.model';
import { compareStringAlphabetical } from '@app/helpers';
import { SetupVoiceGreetingRule, StoreAudioFileRequest, StoreAudioFileResponse } from '@app/models/voice-greeting.model';
import { TransferLabelUsersPayload } from '@app/models/label-management.model';
import { TransferInterestUsersPayload } from '@app/models/interest-management.model';
import { AuditUserEmail, AuditSection, AuditLogsResponse } from '@app/models/history-and-audit-logs.model';

@Injectable()
export class CommonService {
    /**Application's Observable data were stored to stream around. */
    private _appProfileData$!: BehaviorSubject<AppProfileData>;
    private readonly _hasFormChanges$ = new BehaviorSubject<boolean>(false);
    private readonly _isSidebarLoading$ = new BehaviorSubject<boolean>(false);
    private readonly _isLoading$ = new BehaviorSubject<boolean>(false);
    private readonly _isNavLoading$ = new BehaviorSubject<boolean>(false);
    private readonly _sidePanel$ = new BehaviorSubject<boolean>(false);
    private readonly _applicationStatus$ = new BehaviorSubject<AppStatus | null>(null);
    private readonly _screenStatus$ = new BehaviorSubject<AppStatus | null>(null);
    private _tagsData$!: Observable<TagData[]>;
    private readonly _serviceDownBannerData$ = new BehaviorSubject<ServiceDownBannerData[]>([]);
    private readonly _accountLimitsData$ = new BehaviorSubject<AccountLimitsData | null>(null);
    private readonly _orgSelectorList$ = new BehaviorSubject<OrganizationList[] | null>(null);
    private readonly _orgListInProgress$ = new BehaviorSubject<boolean>(false);

    private readonly DEFAULT_TIME_INTERVAL = 15;

    dialogData = { autoFocus: false, restoreFocus: false };
    disabledFeatures!: any;
    envData: any;

    constructor(
        private readonly _httpS: HttpService,
        private readonly _dialog: MatDialog,
        private readonly _snackBar: MatSnackBar,
        private readonly _momentTimezonePipe: MomentTimezonePipe,
        private readonly _localS: LocalStorageService
    ) {
        this.envData = environment;
        this.disabledFeatures = this.envData['disabledFeatures'] || null;
    }

    /**App's data service's getter, setters and functions starts */

    /**App profile data's getter as value*/
    get appProfileDataAsValue(): AppProfileData {
        return this._appProfileData$?.getValue();
    }

    /**App profile data's getter as observable*/
    get appProfileDataAsObservable(): Observable<AppProfileData> {
        return this._appProfileData$;
    }

    /**App profile data's setter*/
    setAppProfileData(data: AppProfileData) {
        if (data.currentOrganization && !data.currentOrganization.timezone) {
            data.currentOrganization = { ...data.currentOrganization, timezone: DEFAULT_TIMEZONE };
        }
        if (!this._appProfileData$) {
            this._appProfileData$ = new BehaviorSubject<AppProfileData>(data);
        } else {
            this._appProfileData$.next(data);
        }
    }

    get _orgListInProgress(): Observable<boolean> {
        return this._orgListInProgress$;
    }

    setOrgListInProgress(data: boolean) {
        this._orgListInProgress$.next(data);
    }

    get _orgSelectorList(): Observable<OrganizationList[] | null> {
        return this._orgSelectorList$;
    }

    setOrgSelectorList(data: OrganizationList[] | null) {
        this._orgSelectorList$.next(data);
    }

    public toggleOrganizationLanguage(langName: string, enabled: boolean): void {
        const appProfile = this._appProfileData$?.getValue();
        if (!appProfile || !appProfile.currentOrganization) return;

        const currentLanguages: string[] = Array.isArray(appProfile.currentOrganization?.languages)
            ? appProfile.currentOrganization.languages.slice()
            : [];

        let updatedLanguages: string[];
        if (enabled) {
            updatedLanguages = Array.from(new Set([...currentLanguages, langName]));
        } else {
            updatedLanguages = currentLanguages.filter(l => l !== langName);
        }

        updatedLanguages.sort(compareStringAlphabetical);

        const updatedProfile: AppProfileData = {
            ...appProfile,
            currentOrganization: {
                ...appProfile.currentOrganization,
                languages: updatedLanguages
            }
        };

        this._appProfileData$.next(updatedProfile);
    }

    get hasFullAccess(): boolean {
        return !!this._appProfileData$.getValue()?.user?.user?.hasFullAccess;
    }

    get hasSnap(): boolean {
        return !!this._appProfileData$.getValue()?.currentOrganization?.snap_enabled;
    }

    get selectedOrgId(): number {
        return this._appProfileData$.getValue()?.currentOrganization?.id || this.getLocalStoredAuthData()?.orgiId;
    }

    /**Selected organization list getter as value*/
    get organizationLists(): OrganizationList[] {
        return this._appProfileData$.getValue().organizations;
    }

    /**Selected organization's getter as observable*/
    get organizationListInfo(): Observable<OrganizationList> {
        return this._appProfileData$.pipe(
            filter(data => !!data?.currentOrganization && data.currentOrganization?.id >= 0),
            map(data => ({
                ...data.currentOrganization
            }))
        );
    }

    /**Selected organization's setter by ID*/
    setOrgId(orgId: number, orgSelectorData: OrganizationList[] | null = null) {
        const appProfileData = this._appProfileData$.getValue();
        if (appProfileData.currentOrganization.id == orgId) return;
        
        const updatedOrganizations = orgSelectorData ?? appProfileData.organizations;

        let currentOrganization: OrganizationList | undefined = updatedOrganizations.find(org => org.id == orgId);
        if (currentOrganization) {
            if (!currentOrganization.timezone) {
                currentOrganization = { ...currentOrganization, timezone: DEFAULT_TIMEZONE };
            }
            let selectedCenter = currentOrganization.seniorCenters?.length ? currentOrganization.seniorCenters[0] : null;
            this._appProfileData$.next({
                ...appProfileData,
                organizations: updatedOrganizations, 
                currentOrganization: currentOrganization,
                currentSeniorCenter: selectedCenter,
                tagData: []
            });
            let authData = new AuthData(
                appProfileData.accessToken,
                currentOrganization.id,
                selectedCenter?.id,
                +currentOrganization.timezone.hour_offset,
                appProfileData.hasNoProgramFiltered
            );
            this._localS.setAuthData(authData);
            this._snackBar.open(`${SNACKBAR_NOTIFICATION.ORGANIZATION_CHANGED_TO} ${currentOrganization.name}`, '', {
                duration: SNACK_TIMEOUT
            });
        }
    }

    getIntercomToken(email: string) {
        return this._httpS.getIntercomAccessToken({email});
    }

    /**
     * Fetches the organization selector list for the current user.
     * If the user's profile organization ID is present, sets the selector list from profile data and returns an empty observable.
     * Otherwise, if the selector list is not loaded and not in progress, triggers an API call to fetch organizations and updates the selector list.
     * Returns an empty observable if the selector list is already loaded or loading is in progress.
     * @returns {Observable<OrganizationList[]>} Observable emitting the organization list or an empty array.
     */
    fetchOrgSelectorList(): Observable<OrganizationList[]> {
        const profileOrgId = this.appProfileDataAsValue?.user?.profile?.organisationId;
        if (profileOrgId != null) {
            this.setOrgSelectorList([...this.appProfileDataAsValue.organizations]);
            return of([]);
        }

        if (this._orgSelectorList$.value === null && !this._orgListInProgress$.value) {
            this.setOrgListInProgress(true);
            return this.getOrganizations()
                .pipe(
                    tap((data: OrganizationList[]) => this.setOrgSelectorList(data)), 
                    finalize(() => this.setOrgListInProgress(false))
                );
        }
        
        return of([]);
    }

    /**Requests User's Organizations
     * BH admins will get list organizations
     * Organization admins will get their organization in array
     */
    getOrganizations(orgId?: number): Observable<OrganizationList[]> {
        let params = new HttpParams();
        let methodName = undefined;
        
        if (orgId !== undefined && orgId !== null) {
            params = params.set("selectedOrgId", orgId as any);
            methodName = AnalyticAPIReqLabels.getOrganizationsAsync;
        }
        return this._httpS.getOrganizations(params, methodName).pipe(map(response => response.data));
    }

    /**Selected seniorcenter's getter*/
    get selectedCenterId(): number {
        return this._appProfileData$.getValue()?.currentSeniorCenter?.id ?? -1;
    }

    /**Selected seniorcenter's setter by ID*/
    setCenterId(centerId: number | null) {
        const appProfileData = this._appProfileData$.getValue();
        let currentCenter: SeniorCenter | null;

        if (centerId === null) {
            if (appProfileData.currentOrganization === centerId) return;

            currentCenter = centerId;
        } else {
            if (appProfileData.currentSeniorCenter?.id == centerId) return;

            currentCenter = appProfileData.currentOrganization.seniorCenters.find(center => center.id == centerId) || null;
        }
        let hasNoProgramFilteredValue = centerId == NoProgramFilterValue.id;
        let authData = new AuthData(
            appProfileData.accessToken,
            appProfileData.currentOrganization.id,
            currentCenter?.id,
            +appProfileData.currentOrganization.timezone.hour_offset,
            hasNoProgramFilteredValue
        );
        this._localS.setAuthData(authData);

        this._appProfileData$.next({
            ...appProfileData,
            currentSeniorCenter: currentCenter,
            hasNoProgramFiltered: hasNoProgramFilteredValue
        });
    }

    /**Requests Organization Senior centers*/
    getSeniorCenters(): Observable<SeniorCenter[]> {
        return this._httpS.getSeniorcenterByOrgId(this.selectedOrgId).pipe(map(response => response.data));
    }

    /**Organization Language's getter*/
    get organizationLanguages(): Observable<string[]> {
        return this._appProfileData$.pipe(map(data => data.currentOrganization.languages));
    }

    /**Requests Organization Languages*/
    getLanguagesByOrgId(): Observable<string[]> {
        return this._httpS.getOrganizationLanguages(this.selectedOrgId).pipe(map(response => response.data));
    }

    getOrganizationFiltersById(broadcast_type?: string): Observable<OrganizationFilters> {
        return this._httpS.getOrganizationFiltersById(this.selectedOrgId, broadcast_type).pipe(map(response => response.data));
    }

    /**Organization Interest's getter*/
    get organizationInterests(): Observable<UserInterest[]> {
        return this._appProfileData$.pipe(map(data => data.currentOrganization.interests));
    }

    /**Requests Organization Interests*/
    getInterestsByOrgId(): Observable<UserInterest[]> {
        return this._httpS.getUserInterests(this.selectedOrgId).pipe(map(response => response.data));
    }

    /**Requests Organization Interests*/
    getInterestDetailsByOrgId(params: Params): Observable<GeneralListDataResponse<UserInterestDetail>> {
        const httpParams = JSON.stringify(params) != '{}' ? this.getHttpParams(params) : undefined;
        return this._httpS.getInterestsDetailsByOrgId(this.selectedOrgId, httpParams);
    }

    /**Requests Organization Interests*/
    createInterest(data: UserInterestPost): Observable<GeneralDataResponse<any>> {
        return this._httpS.createInterest(data).pipe(map(response => response));
    }

    /**Requests Organization Interests*/
    getInterestAssociationsByOrgId(interestId: number): Observable<InterestAssociateWarnDialogModel[]> {
        return this._httpS.getInterestAssociationsByOrgId(interestId, this.selectedOrgId).pipe(
            map(response => {
                return this.convertInterestAssociateTableData(response.data, this.appProfileDataAsValue.currentOrganization);
            })
        );
    }

    /**Users Modification in Interests*/
    getUsersInInterest(
        interestId: number,
        params: Params,
        withPagination: boolean = true
    ): Observable<GeneralListDataResponse<UserInterestDetail>> {
        const httpParams = JSON.stringify(params) != '{}' ? this.getHttpParams(params) : undefined;
        if (!withPagination) {
            return this._httpS.getUsersInInterestNoPagination(this.selectedOrgId, interestId, httpParams);
        }

        return this._httpS.getUsersInInterest(this.selectedOrgId, interestId, httpParams);
    }

    /**Update single interest status (archive/activate)*/
    updateInterestStatus(interestId: number, is_active: boolean): Observable<GeneralDataResponse<any>> {
        return this._httpS.updateInterestStatus(interestId, is_active);
    }

    /**Bulk update interest status (archive/activate)*/
    bulkUpdateInterestStatus(payload: BulkInterestStatusPayload): Observable<GeneralDataResponse<BulkInterestStatusResponse>> {
        return this._httpS.bulkUpdateInterestStatus(payload);
    }

    /**Check if interests have scheduled communications*/
    checkScheduledCommunications(interestIds: number[], clientIds: number[] = []): Observable<GeneralDataResponse<ScheduledCommsCheckResponse>> {
        return this._httpS.checkScheduledCommunications(this.selectedOrgId, interestIds, clientIds);
    }

    /**Check if interest name is duplicate*/
    checkInterestNameDuplicate(payload: InterestDuplicateCheckPayload): Observable<GeneralDataResponse<{ exists: boolean }>> {
        return this._httpS.checkInterestNameDuplicate(this.selectedOrgId, payload);
    }

    /**Get audience dedup check for screening outreach*/
    getAudienceDedupCheck(body: AudienceDedupCheckRequest): Observable<AudienceDedupCheckResponse> {
        return this._httpS.getAudienceDedupCheck(body);
    }

    /**Bulk update organization interests (name and/or is_active) - matches label pattern*/
    updateOrgInterests(interests: UpdateOrgInterestPayload[]): Observable<GeneralDataResponse<{ updatedCount: number; skippedCount: number }>> {
        return this._httpS.updateOrgInterests(interests);
    }

    /**Get active organization interests only*/
    getActiveOrgInterests(): Observable<GeneralDataResponse<any[]>> {
        return this._httpS.getActiveOrgInterests(this.selectedOrgId);
    }

    /**Get scheduled communications for organization interests - matches label pattern*/
    getOrgInterestsScheduledCommunications(orgInterestIds: number[]): Observable<GeneralDataResponse<OrgInterestScheduledCommunications>> {
        return this._httpS.getOrgInterestsScheduledCommunications(orgInterestIds);
    }

    getLanguageByOrgId(): Observable<any> {
        return this._httpS.getOrganizationLanguage(this.selectedOrgId);
    }

    getOrgLanguageWithAudioByOrgId(programId?: number | null) {
        let params: any = {};

        if (programId != null) {
            params.programId = programId;
        }

        return this._httpS.getOrgLanguageWithAudio(this.selectedOrgId, params);
    }

    getGreetingRuleByOrgId() {
        return this._httpS.getGreetingRuleByOrgId(this.selectedOrgId);
    }

    addFallbackAudio(language: string, fallback: boolean) {
        const body = {
            orgId: this.selectedOrgId,
            languageName: language,
            fallback
        };

        return this._httpS.addFallbackAudio(body);
    }

    removeAudio(languageName: string) {
        const params = {
            languageName: languageName.trim()
        };

        return this._httpS.removeAudio(this.selectedOrgId, params);
    }

    removeOverrideAudio(language: string, programId: number | null) {
        const params: any = {
            orgId: String(this.selectedOrgId),
            languageName: language.trim(),
        };

        if (programId !== null) {
            params.programId = String(programId);
        }

        return this._httpS.removeOverrideAudio(params);
    }


    getOrgLanguageWithAudioURL(langName: string, langCode: string) {
        const params = {
            langName,
            langCode,
        };

        return this._httpS.getOrgLanguageWithAudioURL(this.selectedOrgId, params);
    }


    storeAudioFile(payload: StoreAudioFileRequest): Observable<StoreAudioFileResponse> {
        return this._httpS.storeAudioFile(this.selectedOrgId, payload);
    }

    setupVoiceGreetingRule(payload : SetupVoiceGreetingRule): Observable<any> {
        return this._httpS.setupVoiceGreetingRule(payload);
    }

    updateOrganizationLanguage(langCode: string, langName: string, active: boolean): Observable<any> {
        return this._httpS.updateOrganizationLanguage({
            orgId: this.selectedOrgId,
            langCode,
            langName,
            active
        });
    }

    getOrgActivationSetup(): Observable<OrgActivationSetupResponse> {
        return this._httpS.getOrgActivationSetup(this.selectedOrgId);
    }

    getProgramActivationSetup(progId: number): Observable<OrgActivationSetupResponse> {
        return this._httpS.getProgramActivationSetup(this.selectedOrgId, progId);
    }

    saveActivationSetupRule(payload: SaveActivationSetupRulePayload): Observable<any> {
        return this._httpS.saveActivationSetupRule(payload);
    }

    retrieveActivatedUsers(params?: HttpParams): Observable<ActivatedUsersResponse> {
        return this._httpS.retrieveActivatedUsers(this.selectedOrgId, params);
    }

    retrieveInactiveUsersCount(params: HttpParams): Observable<InactiveUsersCountResponse> {
        return this._httpS.retrieveInactiveUsersCount(this.selectedOrgId, params);
    }

    downloadActivatedUsers(params?: HttpParams): Observable<Blob> {
        return this._httpS.downloadActivatedUsers(this.selectedOrgId, params);
    }

    triggerManualActivation(params: HttpParams): Observable<any> {
        return this._httpS.triggerManualActivation(this.selectedOrgId, params);
    }

    getOrganizationAddress(orgId: number): Observable<OrganizationAddressResponse> {
        return this._httpS.getOrganizationAddress(orgId).pipe(map(response => response.data));
    }

    updateOrganizationAddress(orgId: number, payload: any): Observable<any> {
        return this._httpS.updateOrganizationAddress(orgId, payload).pipe(map(response => response.data));
    }

    retrievePortalUsersEmails(): Observable<AuditUserEmail[]> {
        return this._httpS.retrievePortalUsersEmails(this.selectedOrgId);
    }

    retrieveAuditSections(): Observable<AuditSection[]> {
        return this._httpS.retrieveAuditSections(this.selectedOrgId);
    }

    retrieveAuditLogs(params?: HttpParams): Observable<AuditLogsResponse> {
        return this._httpS.retrieveAuditLogs(this.selectedOrgId, params);
    }
    
    downloadAuditLogs(params?: HttpParams): Observable<Blob> {
        return this._httpS.downloadAuditLogs(this.selectedOrgId, params);
    }

    /**Requests Current User */
    getCurrentUser(): Observable<CurrentUser> {
        return this._httpS.getCurrentUser().pipe(map(response => response.data));
    }

    getDashboardReports(orgId: number): Observable<ReportData[]> {
        return this._httpS.getDashboardReport(orgId).pipe(map(response => response.data));
    }

    get getServiceDownBannerValue(): Observable<ServiceDownBannerData[]> {
        return this._serviceDownBannerData$;
    }

    setServiceDownBanner(data: ServiceDownBannerData[]) {
        this._serviceDownBannerData$.next(data);
    }

    getServiceDownBanner(): Observable<ServiceDownBannerData[]> {
        return this._httpS.getServiceDownBanner().pipe(map(response => response.data));
    }

    getConstData(key: string): Observable<ConstData> {
        return this._httpS.getConstData(key).pipe(map(response => response.data));
    }

    getAllTags(refreshTags: boolean = false): Observable<TagData[]> {
        if (refreshTags || !this._tagsData$) {
            this._tagsData$ = this._httpS.getAllTagData().pipe(
                shareReplay(1),
                map(response => response.data)
            );
        }
        return this._tagsData$;
    }

    createTagData(body: CreateTagData): Observable<TagData> {
        return this._httpS.createTagData(body).pipe(map(response => response.data));
    }

    getCustomFields(params: {searchText?: string; status?: CustomFieldStatus;} = {}): Observable<CustomField[]> {
        let qParams = new HttpParams();
        if (params.searchText?.trim() && params.searchText.trim().length > 0) {
            qParams = qParams.set("searchText", params.searchText.trim());
        }
        if (params.status) {
            qParams = qParams.set("status", params.status);
        }

        return this._httpS.getCustomFields(qParams).pipe(map(response => response.data));
    }

    createCustomField(body: CreateCustomFieldPayload): Observable<CustomField> {
        return this._httpS.createCustomField(body).pipe(map(response => response.data));
    }

    updateCustomField(id: number, body: CreateCustomField): Observable<CustomField> {
        return this._httpS.updateCustomField(id, body).pipe(map(response => response.data));
    }

    updateCustomFieldStatus(body: UpdateCustomFieldStatus): Observable<GeneralDataResponse<CustomField[]>> {
        return this._httpS.updateCustomFieldStatus(body);
    }

    getCustomFieldsScheduledCommunication(fieldIds: number[]): Observable<GeneralDataResponse<CustomFieldsScheduledCommunicationResponse>> {
        return this._httpS.getCustomFieldsScheduledCommunication(this.selectedOrgId, fieldIds);
    }

    generateBulkUploadSampleFiles(orgCode: string, orgId: number, snap: boolean, medicaid: boolean): Observable<string> {
        return this._httpS.generateBulkUploadSampleFiles(orgCode, orgId, snap, medicaid).pipe(map(response => response.message));
    }

    getPortalUsersByOrgId(orgId: number, qparams?: HttpParams): Observable<GeneralListDataResponse<PortalUserListItem>> {
        return this._httpS.getPortalUsersByOrgId(orgId, qparams).pipe(
            map(response => {
                if (!response?.data?.rows?.length) {
                    return response;
                } else {
                    return {
                        ...response,
                        data: {
                            ...response.data,
                            rows: response.data.rows.map(record => ({
                                ...record,
                                status: record.status == appValues.lockedout ? appValues.lockedoutLabel : record.status
                            }))
                        }
                    };
                }
            })
        );
    }

    createPortalUser(payload: PortalUser): Observable<GeneralResponse> {
        return this._httpS.createPortalUsers(payload);
    }

    updatePortalUser(payload: PortalUser) : Observable<GeneralResponse>{
        return this._httpS.updatePortalUsers(payload);
    }

    unlockAccount(orgId: number, email: string): Observable<GeneralResponse> {
        return this._httpS.unlockAccount(orgId, email);
    }
    
    updatePortalUserStatus(orgId : number, email: string, status : string): Observable<GeneralResponse> {
        return this._httpS.updatePortalUserStatus(orgId, email, status);
    }

    requestPortalUserForgotPassword(email: string): Observable<GeneralResponse> {
        return this._httpS.requestPortalUserForgotPassword(email);
    }

    resendActivationLink(email: string): Observable<GeneralResponse> {
        return this._httpS.resendActivationLink(email);
    }

    getProgramsByOrgId(params?: HttpParams): Observable<GeneralListDataResponse<SelfServiceProgram>> {
        return this._httpS.getProgramsByOrgId(this.selectedOrgId, params);
    }

    getOrganizationTimezones(): Observable<TimezoneResponse[]> {
        return this._httpS.getOrganizationTimezones(this.selectedOrgId);
    }

    getOrgServiceTypes(): Observable<OrgServiceTypesResponse> {
        return this._httpS.getOrgServiceTypes(this.selectedOrgId);
    }

    createServiceData(payload: CreateServiceData): Observable<GeneralDataResponse<CreateServiceData>> {
        return this._httpS.createServiceData(this.selectedOrgId, payload);
    }

    addSelfServiceProgram(payload: CreateProgramData): Observable<GeneralResponse> {
        return this._httpS.addSelfServiceProgram(this.selectedOrgId, payload);
    }

    updateSelfServiceProgram(programId: number, payload: CreateProgramData): Observable<GeneralResponse> {
        return this._httpS.updateSelfServiceProgram(this.selectedOrgId, programId, payload);
    }

    getProgramUsers(programId: number, params?: HttpParams): Observable<GeneralListDataResponse<ProgramUsersResponse>> {
        return this._httpS.getProgramUsers(this.selectedOrgId, programId, params);
    }

    getOrgUsersForProgram(params?: HttpParams): Observable<GeneralListDataResponse<OrgUserForProgramResponse>> {
        return this._httpS.getOrgUsersForProgram(this.selectedOrgId, params);
    }

    getTransferProgramOptions(programId: string) : Observable<GeneralDataResponse<any>>{
        return this._httpS.getTransferProgramOptions(this.selectedOrgId,programId);
    }

    transferUsers(programId : string,payload:any):Observable<GeneralDataResponse<any>> {
        return this._httpS.transferProgramUsers(this.selectedOrgId,programId, payload);
    }

    transferLabelUsers(labelId: string, payload: TransferLabelUsersPayload[]): Observable<GeneralDataResponse<any>> {
        return this._httpS.transferLabelUsers(this.selectedOrgId, labelId, payload);
    }

    transferInterestUsers(interestId: string, payload: TransferInterestUsersPayload[]): Observable<GeneralDataResponse<any>> {
        return this._httpS.transferInterestUsers(this.selectedOrgId, interestId, payload);
    }

    updateProgramUsers(programId: number, payload: UpdateProgramUsersPayload): Observable<GeneralResponse> {
        return this._httpS.updateProgramUsers(this.selectedOrgId, programId, payload);
    }

    fetchFileFromSignedUrl(signedUrl: string): Observable<ArrayBuffer> {
        return this._httpS.fetchFileFromSignedUrl(signedUrl);
    }

    refreshCurrentOrganization(): Observable<void> {
        const snapshot = this._appProfileData$.getValue();
        const orgId = snapshot.currentOrganization?.id;
        const centerId = snapshot.currentSeniorCenter?.id ?? null;
        return this.getOrganizations().pipe(
            map((organizations: OrganizationList[]) => {
                const org = organizations.find(o => o.id === orgId) || organizations[0];
                if (!org) return;
                const currentOrganization = org.timezone ? org : { ...org, timezone: DEFAULT_TIMEZONE };
                const currentSeniorCenter =
                    currentOrganization.seniorCenters?.find(sc => sc.id === centerId) ??
                    currentOrganization.seniorCenters?.[0] ??
                    null;
                this._appProfileData$.next({
                    ...snapshot,
                    organizations,
                    currentOrganization,
                    currentSeniorCenter
                });
            })
        );
    }

    getUserLabels(params?: Params): Observable<GeneralListDataResponse<UserLabel>> {
        let httpParams: HttpParams | undefined = undefined;
    
        if (params && JSON.stringify(params) !== '{}') {
            httpParams = this.getHttpParams(params);
        }
    
        return this._httpS.getUserLabels(this.selectedOrgId, httpParams);
    }

    getActiveUserLabels(): Observable<GeneralDataResponse<UserLabel[]>> {
        return this._httpS.getActiveUserLabels(this.selectedOrgId);
    }

    addUserLabel(labelName: string): Observable<GeneralDataResponse<UserLabel>> {
        return this._httpS.addUserLabel({ name: labelName, orgId: this.selectedOrgId });
    }

    updateUserLabels(payload: { id: number; orgId: number; active: boolean; name?: string }[]) {
        return this._httpS.updateUserLabels(payload);
    }

    getOrgLabelsScheduledCommunication(labelIds: number[], clientIds: number[] = []) {
        return this._httpS.getOrgLabelsScheduledCommunication(this.selectedOrgId, labelIds, clientIds);
    }

    getProgramScheduledCommunications(programId: string, clientIds: number[] = []): Observable<GeneralDataResponse<any>> {
        return this._httpS.getProgramScheduledCommunications(this.selectedOrgId, programId, clientIds);
    }

    /**Users Modification in Labels*/
    getUsersInLabels(
        labelId: number,
        params: Params,
    ): Observable<GeneralListDataResponse<any>> {
        const httpParams = JSON.stringify(params) != '{}' ? this.getHttpParams(params) : undefined;
        return this._httpS.getUsersInLabels(this.selectedOrgId, labelId, httpParams);
    }

    addOrRemoveUserLabels(payload: any, orgId: number): Observable<GeneralDataResponse<any>> {
        return this._httpS.addOrRemoveUserLabels(payload, orgId);
    }

    get currentAccountLimitsData(): Observable<AccountLimitsData | null> {
        return this._accountLimitsData$;
    }

    get currentAccountLimitsDataAsValue(): AccountLimitsData | null {
        return this._accountLimitsData$.value;
    }

    getAccountLimits(orgId: number = this.appProfileDataAsValue.currentOrganization.id): Observable<AccountLimitsData> {
        return this._httpS.getAccountLimits(orgId).pipe(
            tap(response => {
                this._accountLimitsData$.next(!response?.plan?.length ? null : response);
            })
        );
    }

    getLocalStoredAuthData() {
        const authData = JSON.parse(localStorage.getItem('authData') ?? '{}');
        console.log(authData);
        return authData !== '' ? authData : null;
    }

    getAccountFeatureLimits(
        featureName: string,
        orgId: number = this.appProfileDataAsValue?.currentOrganization?.id || this.selectedOrgId
    ): Observable<AccountFeatureLimitsData> {
        return this._httpS.getAccountFeatureLimits(orgId, featureName);
    }

    acceptTermsPolicy() {
        return this._httpS.acceptTermsPolicy().pipe(
            switchMap(response => {
                return this.getCurrentUser();
            }),
            tap(userData => {
                this.setAppProfileData({
                    ...this.appProfileDataAsValue,
                    user: userData
                });
            })
        );
    }

    translateTextByLanguage(
        text: string,
        targetLanguage: string,
        sourceLanguage: string = 'English'
    ): Observable<PreviewLanguageTranslationData> {
        let previewData: PreviewLanguageTranslationData = {
            previewLanguage: targetLanguage,
            translatedMessage: null,
            errorData: null
        };
        return this._httpS.translateTextByLanguage({ sourceLanguage, targetLanguage, text }).pipe(
            map(response => {
                if (response.data) previewData.translatedMessage = response.data;
                return previewData;
            }),
            catchError(error => {
                previewData.errorData = !text.length
                    ? { type: 'warn', message: GENERAL_MESSAGES.LANG_TRANSLATE_BODY_REQ }
                    : { type: 'error', message: GENERAL_MESSAGES.LANG_TRANSLATE_FAIL };
                return of(previewData);
            })
        );
    }

    //************************* TABLEAU ROUTES ******************************//

    getTableauJwtToken() {
        const orgId = this.appProfileDataAsValue?.currentOrganization?.id || this.selectedOrgId;
        return this._httpS.getTableauJwtToken({orgId});
    }

    validateTableauSite(orgId: string) {
        return this._httpS.validateTableauSite(orgId)
    }

    /**App's data service's getter, setters and functions ends */

    /**Common service's getter, setters and functions starts */
    get isSidebarLoading(): Observable<boolean> {
        return this._isSidebarLoading$;
    }

    setSidebarLoading(value: boolean) {
        this._isLoading$.next(value);
    }

    get isLoading(): Observable<boolean> {
        return this._isLoading$;
    }

    setLoading(value: boolean) {
        this._isLoading$.next(value);
    }

    get isNavLoading(): Observable<boolean> {
        return this._isNavLoading$;
    }

    setNavLoading(value: boolean) {
        this._isNavLoading$.next(value);
    }

    get hasFormChanges(): Observable<boolean> {
        return this._hasFormChanges$;
    }

    setFormChanges(value: boolean) {
        this._hasFormChanges$.next(value);
    }

    get isSidePanelOpen(): Observable<boolean> {
        return this._sidePanel$;
    }

    setSidePanel(value: boolean) {
        this._sidePanel$.next(value);
    }

    get applicationStatus(): Observable<AppStatus | null> {
        return this._applicationStatus$;
    }

    setApplicationStatus(value: AppStatus) {
        this._applicationStatus$.next(value);
    }

    get screenStatus(): Observable<AppStatus | null> {
        return this._screenStatus$;
    }

    setScreenStatus(value: AppStatus | null) {
        this._screenStatus$.next(value);
    }

    /**validating the errors according to its fields of the form
     * and return the error messages
     */
    getFormErrors(form: AbstractControl, errFields: any, errMsgs: any) {
        if (!form) return;
        for (const field in errFields) {
            errFields[field] = '';
            const control = form.get(field);
            if (control && control.dirty && !control.valid) {
                const messages = errMsgs[field];
                for (const key in control.errors) {
                    errFields[field] = messages[key];
                }
            }
        }
        return errFields;
    }

    /**Trigger the dialog component with basic info
     * { message: string;
     * title: string;
     * actions: { cancel: string, ok: string } }
     * width: string, height: string
     */
    openDialog(data: DialogData, width: string, height: string, config?: MatDialogConfig) {
        const newDialog = {
            ...this.dialogData,
            data,
            width,
            height: 'auto',
            ...config
        };
        return this._dialog.open(DialogComponent, newDialog).afterClosed();
    }

    /**Convert Params type to HttpParams */
    getHttpParams(params: Params): HttpParams {
        let fetchedParams = new HttpParams();
        for (let key in params) {
            fetchedParams = fetchedParams.set(key, params[key]);
        }
        return fetchedParams;
    }

    /**
     * Function to generate timeData to dropdowns
     * @returns Array of TimeData
     */
    generateTimeData(interval: number = this.DEFAULT_TIME_INTERVAL): TimeData[] {
        interval = interval > 0 ? interval : this.DEFAULT_TIME_INTERVAL;
        const time_arr = [];

        const formatTimeUnit = (unit: number): string => (unit <= 9 ? `0${unit}` : unit.toString());

        for (let hrs = 0; hrs < 24; hrs++) {
            for (let mins = 0; mins < 60; mins += interval) {
                const _dateTime = new Date();
                _dateTime.setHours(hrs, mins);

                const _hrs = _dateTime.getHours() % 12 || 12; // Convert 0 to 12 for 12-hour format
                const _formattedHrs = formatTimeUnit(_hrs);
                const _formattedMins = formatTimeUnit(_dateTime.getMinutes());

                time_arr.push({
                    label: `${_formattedHrs}:${_formattedMins} ${hrs < 12 ? 'AM' : 'PM'}`,
                    hours: hrs,
                    minutes: mins
                });
            }
        }

        return time_arr;
    }

    /**
     * Function to convert default table filter data to data with marked filter items
     * based on its argument values below
     * @param filterKeys Ordered as per tableFilterData items
     * @param paramValues Values fetched from params
     * @param filterData Current table filter data
     * @returns marked table filter data
     */
    setTableFilterValues(filterKeys: string[], paramValues: { [k: string]: any }, filterData: TableFilterData[]): TableFilterData[] {
        //converting strings values into array
        for (const paramKey in paramValues) {
            if (paramValues.hasOwnProperty(paramKey) && filterKeys.includes(paramKey)) {
                if (typeof paramValues[paramKey] == 'object' && paramValues[paramKey]?.length) {
                    paramValues[paramKey] = paramValues[paramKey]; // NOSONAR
                } else {
                    paramValues[paramKey] = [paramValues[paramKey]];
                }
            }
        }

        //marking the checked flag in filter options
        filterKeys.forEach((fltKey, index) => {
            if (paramValues[fltKey]) {
                filterData[index] = {
                    ...filterData[index],
                    options: filterData[index]?.options.map(opt => ({
                        ...opt,
                        checked: paramValues[fltKey].some((pvalue: string) => {
                            return pvalue == opt.value;
                        })
                    }))
                };
            } else {
                filterData[index] = {
                    ...filterData[index],
                    options: filterData[index]?.options.map(opt => ({
                        ...opt,
                        checked: false
                    }))
                };
            }
        });

        return filterData;
    }

    convertInterestAssociateTableData(
        data: UserInterestAssociatedWarn,
        orgData: OrganizationList,
        timeFormat: string = DATE_TIME_FORMATS.fullDateInNum
    ): InterestAssociateWarnDialogModel[] {
        let timezoneIdentifier = orgData.timezone ? orgData.timezone.timezone_identifier : DEFAULT_TIMEZONE.timezone_identifier;
        return [
            {
                ...MANAGE_INTERESTS_DATA.programs,
                tableData: data.programs.map(record => {
                    if (record.name == null) {
                        record.name = NoProgramFilterValue.name;
                    }
                    return record;
                })
            },
            {
                ...MANAGE_INTERESTS_DATA.broadcasts,
                tableData: data.broadcasts.map(record => ({
                    ...record,
                    sent_date: this._momentTimezonePipe.transform(record.sent_date, timezoneIdentifier, timeFormat)
                }))
            },
            {
                ...MANAGE_INTERESTS_DATA.surveys,
                tableData: data.surveys.map(({ survey, audience, last_run_date, ...rest }) => ({
                    ...rest,
                    name: survey?.name ?? '',
                    audience: audience ?? orgData.name,
                    last_run_date: this._momentTimezonePipe.transform(last_run_date, timezoneIdentifier, timeFormat)
                }))
            },
            {
                ...MANAGE_INTERESTS_DATA.reminders,
                tableData: data.eventReminders.map(({ program, active, deliveryLogs, ...rest }) => ({
                    ...rest,
                    program: program ?? orgData.name,
                    active: active ? 'Yes' : 'No',
                    sent_date: moment.utc(deliveryLogs?.dateSent ?? '').format(timeFormat)
                }))
            }
        ];
    }

    isUniversalSurvey(surveyId: number): boolean {
        return this.appProfileDataAsValue?.user?.universal_surveys?.includes(surveyId);
    }

    /**Common service's getter, setters and functions ends */

    /**Feature flags getters starts */

    get tagSelectorFeatureFlag() {
        return !this.disabledFeatures?.tagSelection;
    }

    get chatGPTMsgFeatureFlag() {
        return !this.disabledFeatures?.chatGPTMsg;
    }

    get manageInterestFeatureFlag() {
        return !this.disabledFeatures?.manageInterest;
    }

    get orgUserFeatureFlag() {
        return !this.disabledFeatures?.orgUserV2;
    }

    get surveyResponseVFeatureFlag() {
        return !this.disabledFeatures?.surveyResponseV;
    }

    get mobileVersionVFeatureFlag() {
        return !this.disabledFeatures?.mobileVersion;
    }

    get usersUploadCenterFeatureFlag() {
        return !this.disabledFeatures?.usersUploadCenter;
    }

    get surveyQuestionConditionLogicFeatureFlag() {
        return true;
    }

    get isAppInMaintenance(): boolean {
        const envData: any = environment;
        return envData['inMaintanence'] || false;
    }


}
